import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

export const useGlobalCounter = (counterName = 'layer_recommendations') => {
  const [count, setCount] = useState(null); // Start with null to avoid showing fallback
  const [isLoading, setIsLoading] = useState(true);
  const lastIncrementTime = useRef(0);
  const channelRef = useRef(null);
  const incrementInProgress = useRef(false);
  const pendingIncrements = useRef(0);
  const flushTimeoutRef = useRef(null);

  // Fetch initial count
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const { data, error } = await supabase
          .from('global_counters')
          .select('count')
          .eq('name', counterName)
          .single();

        if (error) throw error;
        setCount(data.count);
      } catch (error) {
        console.error('Error fetching counter:', error);
        // Use fallback value only on error
        setCount(95154);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCount();
  }, [counterName]);

  // Poll for latest count every 60s — the counter is decorative, not load-bearing.
  // Was 15s, which at 100 WAU × ~60s avg session = ~24k extra reads/week for a vanity number.
  // Also pause polling when the tab is hidden (Sonner principle: handle edge cases invisibly).
  useEffect(() => {
    let cancelled = false;
    const fetchLatest = async () => {
      if (document.hidden) return;
      try {
        const { data, error } = await supabase
          .from('global_counters')
          .select('count')
          .eq('name', counterName)
          .single();

        if (error) throw error;
        if (cancelled) return;
        // Only update if server has a higher value to avoid UI "drops"
        setCount(prev => (data?.count > (prev ?? 0) ? data.count : prev));
      } catch (error) {
        console.error('Error polling counter:', error);
      }
    };

    const interval = setInterval(fetchLatest, 60000);
    const onVisibility = () => { if (!document.hidden) fetchLatest(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [counterName]);

  const scheduleFlush = useCallback(() => {
    if (flushTimeoutRef.current) return;
    flushTimeoutRef.current = setTimeout(async () => {
      flushTimeoutRef.current = null;
      const toSend = pendingIncrements.current;
      pendingIncrements.current = 0;
      if (toSend <= 0) return;
      // Cap at 200 RPCs per flush so a malformed 365-day trip can't
      // hammer Supabase. Anything above the cap is dropped from the
      // global count; the local UI already shows the accurate number.
      const capped = Math.min(toSend, 200);
      try {
        // Send N increments serially so the backend reflects each
        // layer-day recommendation, not just one "trip submitted" event.
        // The supabase increment_counter RPC takes no amount param, so
        // we loop. Local UI is already optimistic; this just makes the
        // global counter eventually consistent with reality.
        for (let i = 0; i < capped; i++) {
          // eslint-disable-next-line no-await-in-loop
          await supabase.rpc('increment_counter', { counter_name: counterName });
        }
      } catch (error) {
        console.error('Error flushing counter increment:', error);
      }
    }, 15000);
  }, [counterName]);

  // `count` defaults to 1 so existing callers (today, share, etc.) keep working.
  // Trip passes the total number of layer-day recommendations served.
  const increment = useCallback((count = 1) => {
    const n = Math.max(1, Math.floor(count));
    lastIncrementTime.current = Date.now();

    // Optimistic local update for instant UI feedback.
    setCount(prev => (prev == null ? n : prev + n));

    // Batch into one flush window; the flush sends N RPCs.
    pendingIncrements.current += n;
    scheduleFlush();
  }, [scheduleFlush]);

  return { count, increment, isLoading };
};
